import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, AlertTriangle, CheckCircle2, Download, FileText, XCircle } from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { ntaService } from '../services/ntaService';
import {
  fmtMoney, linesToCsv, pivotQuantities, pivotScenario, scenariosOf, toNum,
  type NtaLineRow, type NtaResultRow, type NtaRun,
} from '../lib/nta';
import { NTA_ADVANCE, nextNtaStep, ntaLinesXlsxRows, ntaToday } from '../lib/ntaPortfolio';
import { exportMultiSheetXLSX } from '../utils/exportUtils';
import { NtaStatusBadge } from './NtaPage';
import { NewBadge } from '../components/NewBadge';

// One NMI's NTA (spec 8.2): every scenario with its eligibility and 12-month
// total, and per scenario the component × month breakdown an adviser uses to
// defend the savings figure. The raw 26-column rows download as CSV or XLSX.
// After issue the result is marked accepted, then finalised, each on a date.

const MONTH_FMT = new Intl.DateTimeFormat('en-AU', { month: 'short', year: '2-digit', timeZone: 'UTC' });
const monthLabel = (d: string) => MONTH_FMT.format(new Date(`${d}T00:00:00Z`));
const fmtDate = (d: string | null) =>
  d ? new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${d}T00:00:00Z`)) : '—';
const fmtQty = (n: number | undefined) =>
  n == null ? '' : n.toLocaleString('en-AU', { maximumFractionDigits: 1 });
const fmtCell = (n: number | undefined) =>
  n == null ? '' : n === 0 ? '–' : n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const NtaResultPage = () => {
  const { resultId = '' } = useParams();
  const [result, setResult] = useState<NtaResultRow | null>(null);
  const [run, setRun] = useState<NtaRun | null>(null);
  const [lines, setLines] = useState<NtaLineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [view, setView] = useState<'cost' | 'usage'>('cost');
  const [releasing, setReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [stepDate, setStepDate] = useState(ntaToday);

  const release = async (fn: (id: string) => ReturnType<typeof ntaService.approve>) => {
    setReleasing(true);
    setReleaseError(null);
    const res = await fn(resultId);
    if (!res.success) setReleaseError(res.error || 'Action failed');
    const fresh = await ntaService.getResult(resultId);
    if (fresh.success && fresh.data) setResult(fresh.data.result);
    setReleasing(false);
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      const [res, linesRes] = await Promise.all([ntaService.getResult(resultId), ntaService.getLines(resultId)]);
      if (!mounted) return;
      if (!res.success) setError(res.error || 'Failed to load NTA result');
      else if (!linesRes.success) setError(linesRes.error || 'Failed to load NTA lines');
      setResult(res.data?.result ?? null);
      setRun(res.data?.run ?? null);
      setLines(linesRes.data ?? []);
      setLoading(false);
    };
    void load();
    return () => { mounted = false; };
  }, [resultId]);

  const scenarios = useMemo(() => scenariosOf(lines), [lines]);
  const baseline = scenarios.find((s) => s.isBaseline) ?? null;
  const current = scenarios.find((s) => s.scenario === active) ?? scenarios[0] ?? null;
  const pivot = useMemo(() => (current ? pivotScenario(current.lines) : null), [current]);
  const qty = useMemo(() => (current ? pivotQuantities(current.lines) : null), [current]);

  const downloadCsv = () => {
    const blob = new Blob([linesToCsv(lines)], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `NTA_${result?.nmi ?? resultId}_${run?.run_date ?? ''}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadXlsx = () => {
    exportMultiSheetXLSX([{ name: 'Lines', rows: ntaLinesXlsxRows(lines) }], `NTA_${result?.nmi ?? resultId}_${run?.run_date ?? ''}`);
  };

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading NTA result…</div>;
  if (error || !result) {
    return (
      <div className="p-6">
        <Link to="/bill-validation/nta" className="text-xs text-gray-500 hover:underline">← Network Tariff Analysis</Link>
        <div className="mt-3 flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> {error || 'NTA result not found'}
        </div>
      </div>
    );
  }

  const saving = toNum(result.savings);
  const client = result.site?.client ?? null;
  const reportable = saving != null && saving > 0 && client;
  const released = ['issued', 'accepted', 'finalised'].includes(result.status);
  const nextStep = nextNtaStep(result.status);

  return (
    <div className="p-6 space-y-4">
      <div>
        <Link to={`/bill-validation/nta?run=${result.run_id}`} className="text-xs text-gray-500 hover:underline">
          ← Network Tariff Analysis
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-baseline gap-3">
            <h1 className="font-mono text-xl font-semibold text-gray-900">{result.nmi}</h1>
            <NewBadge />
            <NtaStatusBadge status={result.status} />
            {result.source === 'energetica' && (
              <span className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700">Imported · Energetica</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={downloadCsv} disabled={lines.length === 0}>
              <Download className="h-4 w-4" /> Download CSV
            </Button>
            <Button size="sm" variant="outline" onClick={downloadXlsx} disabled={lines.length === 0} data-testid="nta-result-xlsx">
              <Download className="h-4 w-4" /> Export XLSX <NewBadge />
            </Button>
            {reportable ? (
              <Button size="sm" asChild>
                <Link to={`/bill-validation/nta/report?run=${result.run_id}&client=${client.id}`}>
                  <FileText className="h-4 w-4" /> Client report
                </Link>
              </Button>
            ) : (
              <Button size="sm" disabled title="A client report needs a positive saving and a linked client">
                <FileText className="h-4 w-4" /> Client report
              </Button>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-500">
          {[client?.name, result.site?.addr_street, result.site?.addr_city, result.market_region].filter(Boolean).join(' · ')}
          {result.site_id && (
            <> · <Link to={`/bill-validation/sites/${result.site_id}`} className="hover:underline">Site</Link></>
          )}
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className={cn('rounded-lg border px-4 py-3', saving != null && saving > 0 ? 'border-green-200 bg-green-50' : 'bg-white')}>
          <div className="text-xs text-gray-500">Annual saving</div>
          <div data-testid="nta-result-saving" className="mt-1 text-2xl font-semibold text-gray-900">
            {saving != null && saving > 0 ? fmtMoney(saving) : 'None'}
          </div>
          <div className="text-xs text-gray-500">
            {result.baseline_code ?? '—'} → {result.best_code ?? 'no eligible alternative'}
          </div>
        </div>
        <div className="rounded-lg border bg-white px-4 py-3">
          <div className="text-xs text-gray-500">Projection</div>
          <div className="mt-1 text-sm text-gray-900">{fmtDate(result.projection_from)} – {fmtDate(result.projection_to)}</div>
        </div>
        <div className="rounded-lg border bg-white px-4 py-3">
          <div className="text-xs text-gray-500">Source interval data</div>
          <div className="mt-1 text-sm text-gray-900">{fmtDate(result.source_from)} – {fmtDate(result.source_to)}</div>
        </div>
        <div className="rounded-lg border bg-white px-4 py-3">
          <div className="text-xs text-gray-500">Run</div>
          <div className="mt-1 text-sm text-gray-900">{run?.label ?? '—'}</div>
          <div className="text-xs text-gray-500">{run?.run_date}</div>
        </div>
      </div>

      {(result.status === 'cannot_assess' || result.status === 'failed' || result.status === 'no_data') && result.reasons.length > 0 && (
        <div data-testid="nta-cannot-assess" className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">Why this site could not be assessed</div>
            <ul className="mt-1 list-disc pl-4">
              {result.reasons.map((r) => <li key={r}>{r}</li>)}
            </ul>
          </div>
        </div>
      )}

      {(result.baseline_not_viable || result.error) && (
        <div className="space-y-2">
          {result.baseline_not_viable && (
            <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              The current tariff is no longer eligible for this site{baseline?.reason ? `: ${baseline.reason}` : '.'}
            </div>
          )}
          {result.error && (
            <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="whitespace-pre-wrap break-all">{result.error}</span>
            </div>
          )}
        </div>
      )}

      <div className="rounded-lg border bg-white">
        <div className="border-b px-4 py-3 text-sm font-medium text-gray-900">Scenarios</div>
        <table data-testid="nta-scenarios" className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
              <th className="px-4 py-2 font-medium">Scenario</th>
              <th className="px-4 py-2 font-medium">Eligible</th>
              <th className="px-4 py-2 font-medium">Reason</th>
              <th className="px-4 py-2 text-right font-medium">12-month cost (inc GST)</th>
              <th className="px-4 py-2 text-right font-medium">vs current</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {scenarios.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">No scenario rows.</td></tr>
            )}
            {scenarios.map((s) => {
              const delta = baseline && !s.isBaseline ? Math.round((s.total - baseline.total) * 100) / 100 : null;
              const isBest = s.tariffCode === result.best_code && !s.isBaseline;
              return (
                <tr
                  key={s.scenario}
                  data-testid="nta-scenario"
                  className={cn('cursor-pointer hover:bg-gray-50', current?.scenario === s.scenario && 'bg-blue-50/60')}
                  onClick={() => setActive(s.scenario)}
                >
                  <td className="px-4 py-2.5 font-medium text-gray-900">
                    {s.scenario}
                    {isBest && <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">Best</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    {s.eligible
                      ? <CheckCircle2 className="h-4 w-4 text-green-600" aria-label="Eligible" />
                      : <XCircle className="h-4 w-4 text-red-500" aria-label="Not eligible" />}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{s.reason ?? ''}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtMoney(s.total)}</td>
                  <td className={cn('px-4 py-2.5 text-right tabular-nums',
                    delta == null ? 'text-gray-400' : delta < 0 ? 'text-green-700' : 'text-red-600')}>
                    {delta == null ? '—' : `${delta > 0 ? '+' : ''}${fmtMoney(delta)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {current && pivot && (
        <div className="rounded-lg border bg-white">
          <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
            <span className="text-sm font-medium text-gray-900">Monthly breakdown</span>
            <div className="flex overflow-hidden rounded border text-xs" role="tablist">
              {(['cost', 'usage'] as const).map((v) => (
                <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => setView(v)}
                  className={cn('px-2.5 py-0.5', view === v ? 'bg-gray-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-50')}>
                  {v === 'cost' ? 'Cost $' : 'Usage & demand'}
                </button>
              ))}
            </div>
            <span className="mx-1 h-4 w-px bg-gray-200" />
            {scenarios.map((s) => (
              <button
                key={s.scenario}
                type="button"
                onClick={() => setActive(s.scenario)}
                className={cn('rounded-full border px-3 py-0.5 text-xs',
                  current.scenario === s.scenario ? 'border-[#3790C5] bg-[#3790C5] text-white' : 'bg-white text-gray-700 hover:bg-gray-50')}
              >
                {s.tariffCode}
              </button>
            ))}
          </div>
          {view === 'usage' && qty && (
            <div className="overflow-x-auto">
              {qty.rows.length === 0 ? (
                <p className="px-4 py-3 text-sm text-gray-500">No usage or demand quantities on this scenario.</p>
              ) : (
                <table data-testid="nta-usage" className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-gray-50 text-gray-500">
                      <th className="sticky left-0 bg-gray-50 px-3 py-2 text-left font-medium">Component</th>
                      <th className="px-3 py-2 text-left font-medium">Unit</th>
                      {qty.months.map((m) => (
                        <th key={m} className="px-3 py-2 text-right font-medium whitespace-nowrap">{monthLabel(m)}</th>
                      ))}
                      <th className="px-3 py-2 text-right font-medium" title="kWh: 12-month sum · kW/kVA: 12-month peak">Total / Peak</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {qty.rows.map((row) => (
                      <tr key={row.component}>
                        <td className="sticky left-0 bg-white px-3 py-1.5 whitespace-nowrap text-gray-800">{row.component}</td>
                        <td className="px-3 py-1.5 text-gray-500">{row.unit}</td>
                        {qty.months.map((m) => (
                          <td key={m} className="px-3 py-1.5 text-right tabular-nums text-gray-700">{fmtQty(row.byMonth[m])}</td>
                        ))}
                        <td className="px-3 py-1.5 text-right font-medium tabular-nums">{fmtQty(row.total)}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 bg-gray-50 font-medium">
                      <td className="sticky left-0 bg-gray-50 px-3 py-2">Total usage</td>
                      <td className="px-3 py-2 text-gray-500">kWh</td>
                      {qty.months.map((m) => (
                        <td key={m} className="px-3 py-2 text-right tabular-nums">{fmtQty(qty.usageByMonth[m])}</td>
                      ))}
                      <td className="px-3 py-2 text-right tabular-nums">{fmtQty(qty.usageTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          )}
          {view === 'cost' && <div className="overflow-x-auto">
            <table data-testid="nta-breakdown" className="w-full text-xs">
              <thead>
                <tr className="border-b bg-gray-50 text-gray-500">
                  <th className="sticky left-0 bg-gray-50 px-3 py-2 text-left font-medium">Component</th>
                  {pivot.months.map((m) => (
                    <th key={m} className="px-3 py-2 text-right font-medium whitespace-nowrap">{monthLabel(m)}</th>
                  ))}
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pivot.rows.map((row) => (
                  <Fragment key={row.component}>
                    <tr>
                      <td className="sticky left-0 bg-white px-3 py-1.5 whitespace-nowrap text-gray-800">{row.component}</td>
                      {pivot.months.map((m) => (
                        <td key={m} className="px-3 py-1.5 text-right tabular-nums text-gray-700">{fmtCell(row.byMonth[m])}</td>
                      ))}
                      <td className="px-3 py-1.5 text-right font-medium tabular-nums">{fmtCell(row.total)}</td>
                    </tr>
                  </Fragment>
                ))}
                <tr className="border-t-2 bg-gray-50 font-medium">
                  <td className="sticky left-0 bg-gray-50 px-3 py-2">Total</td>
                  {pivot.months.map((m) => (
                    <td key={m} className="px-3 py-2 text-right tabular-nums">{fmtCell(pivot.monthTotals[m])}</td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums">{fmtCell(pivot.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>}
        </div>
      )}

      {result.source === 'engine' && (
        <div className="flex flex-wrap items-center justify-end gap-2" data-testid="nta-release">
          {releaseError && <span className="text-sm text-red-600">{releaseError}</span>}
          {released && (
            <span className="text-sm text-gray-600" data-testid="nta-steps">
              {[
                ['Issued', result.issued_at],
                ['Accepted', result.accepted_at],
                ['Finalised', result.finalised_at],
              ].filter(([, at]) => at).map(([label, at]) => `${label} ${fmtDate(at!.slice(0, 10))}`).join(' · ')}
              {' '}— figures are locked. Re-run NTA to produce a new result.
            </span>
          )}
          {nextStep && (
            <>
              <label className="flex items-center gap-1 text-xs text-gray-600">
                Date
                <input
                  type="date"
                  data-testid="nta-step-date"
                  className="rounded border px-2 py-1 text-sm"
                  value={stepDate}
                  max={ntaToday()}
                  min={(nextStep === 'accepted' ? result.issued_at : result.accepted_at)?.slice(0, 10)}
                  onChange={(e) => setStepDate(e.target.value)}
                />
              </label>
              <Button size="sm" disabled={releasing || !stepDate} data-testid={`nta-mark-${nextStep}`}
                onClick={() => void release((id) => (nextStep === 'accepted'
                  ? ntaService.markAccepted(id, stepDate)
                  : ntaService.markFinalised(id, stepDate)))}>
                {NTA_ADVANCE[nextStep].action} <NewBadge />
              </Button>
            </>
          )}
          {!released && <Button size="sm" variant="outline" disabled={releasing || result.status !== 'calculated'}
            title={result.status === 'calculated' ? 'Mark this result reviewed and correct' : 'Only a result with a saving can be approved'}
            onClick={() => void release(ntaService.approve)}>
            Approve
          </Button>}
          {!released && <Button size="sm" disabled={releasing || result.status !== 'approved'}
            title={result.status === 'approved' ? 'Lock the result; the client report can then be sent' : 'Approve first'}
            onClick={() => { if (window.confirm('Issue this result? It cannot be changed afterwards.')) void release(ntaService.issue); }}>
            Issue
          </Button>}
        </div>
      )}
    </div>
  );
};
